import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { UserPlus, Link2, Copy, Share2 } from 'lucide-react';
import client from '../../api/client';
import { useTranslation } from 'react-i18next';

function InviteSection() {
  const { t } = useTranslation();
  const [generatedLink, setGeneratedLink] = useState('');
  const [attachAsClient, setAttachAsClient] = useState(true);

  const generateLinkMutation = useMutation({
    mutationFn: (inviteType) => client.post('/invites/send', { invite_type: inviteType }),
    onSuccess: (res) => {
      setGeneratedLink(res.data.invite_url);
      toast.success(t('clients.inviteLinkGenerated'));
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || t('clients.inviteCreateFailed'));
    },
  });

  const generateLink = () => {
    setGeneratedLink('');
    generateLinkMutation.mutate(attachAsClient ? 'client_assignment' : 'referral');
  };
  const sending = generateLinkMutation.isPending;

  const copyLink = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(generatedLink).then(() => {
        toast.success(t('clients.linkCopied'));
      }).catch(() => {
        fallbackCopy(generatedLink);
      });
    } else {
      fallbackCopy(generatedLink);
    }
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); toast.success(t('clients.linkCopied')); }
    catch { toast.error(t('clients.copyFailed')); }
    document.body.removeChild(ta);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'trainedbyRS Invite', text: 'Join me on trainedbyRS!', url: generatedLink });
      } catch (e) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <Card className="border-border/50 border-dashed">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-primary" />
          {t('clients.inviteNewClient')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-3 p-2 rounded-lg border border-border/40">
          <div className="min-w-0">
            <Label htmlFor="attach-as-client" className="text-xs font-medium">
              {t('clients.attachAsClient')}
            </Label>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
              {attachAsClient ? t('clients.attachAsClientOnHint') : t('clients.attachAsClientOffHint')}
            </p>
          </div>
          <Switch
            id="attach-as-client"
            checked={attachAsClient}
            onCheckedChange={setAttachAsClient}
            data-testid="attach-as-client-toggle"
          />
        </div>
        <Button
          onClick={generateLink}
          size="sm"
          className="bg-primary hover:bg-primary/90"
          disabled={sending}
          data-testid="send-client-invite-btn"
        >
          <Link2 className="h-4 w-4 mr-1" />
          {sending ? t('common.generating') : t('clients.generateInviteLink')}
        </Button>
        {generatedLink && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/30" data-testid="client-generated-link-box">
            <input
              value={generatedLink}
              readOnly
              className="flex-1 text-xs bg-transparent border-none outline-none truncate"
              data-testid="client-generated-link-input"
            />
            <Button size="sm" variant="ghost" onClick={copyLink} className="shrink-0 h-11 w-11 md:h-7 md:w-7 p-0" data-testid="copy-client-invite-link-btn" title="Copy link">
              <Copy className="h-3 w-3" />
            </Button>
            <Button size="sm" onClick={shareLink} className="shrink-0 h-11 md:h-7 bg-primary hover:bg-primary/90 text-xs px-2 md:hidden" data-testid="share-client-invite-link-btn" title="Share link">
              <Share2 className="h-3 w-3 mr-1" />
              {t('common.share')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default InviteSection;
